import { z } from "zod";

import type {
  CNSActionContinuationReservationResponse,
  CNSActionSubmissionResponse,
} from "@/models/cns";
import { actionReceiptWireSchema } from "@/types/actionReceipt.schema";
import { workflowSnapshotWireSchema } from "@/types/workflowSnapshot.schema";

export { actionReceiptWireSchema } from "@/types/actionReceipt.schema";

export const completedAlternativeActionRequestWireSchema = z
  .object({
    actionRequestId: z.string().trim().min(1),
    assistantMessagePublicId: z.string().trim().min(1),
    streamPartId: z.string().trim().min(1),
    receipt: actionReceiptWireSchema,
  })
  .strict();

/** Runtime response contract for an addressed Action Request submission. */
export const actionSubmissionResponseWireSchema = z
  .object({
    receipt: actionReceiptWireSchema,
    completedAlternativeRequests: z.array(
      completedAlternativeActionRequestWireSchema,
    ),
    workflowSnapshot: workflowSnapshotWireSchema,
    continuationMode: z.enum(["OPTIONAL", "EXPLICIT", "NONE"]),
    replayed: z.boolean(),
  })
  .strict() satisfies z.ZodType<CNSActionSubmissionResponse>;

export function parseActionSubmissionResponseWire(
  input: unknown,
): CNSActionSubmissionResponse {
  const parsed = actionSubmissionResponseWireSchema.safeParse(input);
  if (!parsed.success) {
    throw new Error(
      "Action response was invalid. Refresh to restore the authoritative result.",
    );
  }
  return parsed.data;
}

/** Browser request used only to choose the parent of the server-owned continuation message. */
export interface ActionContinuationReservationRequest {
  parentMessageId: string | null;
}

/** Idempotent result of reserving one assistant continuation for an Action Request. */
export const actionContinuationReservationResponseWireSchema = z
  .object({
    assistantMessageId: z.string().trim().min(1),
    parentMessageId: z.string().trim().min(1).nullable(),
    status: z.enum(["READY", "RUNNING", "COMPLETED"]),
  })
  .strict() satisfies z.ZodType<CNSActionContinuationReservationResponse>;

export type ActionContinuationReservationResponse =
  CNSActionContinuationReservationResponse;

export function parseActionContinuationReservationResponseWire(
  input: unknown,
): ActionContinuationReservationResponse {
  const parsed =
    actionContinuationReservationResponseWireSchema.safeParse(input);
  if (!parsed.success) {
    throw new Error(
      "Continuation reservation was invalid. Refresh to restore the authoritative conversation.",
    );
  }
  return parsed.data;
}
